import type { ReactNode } from 'react'

import { useId } from 'react'
import { cn } from '@/lib/utils'

/** Optional class names for each part of the mark, so the intro can draw it piece by piece. */
export type LensMarkParts = {
    ring?: string
    crescent?: string
    orbit?: string
    trails?: string
    star?: string
}

interface GlyphProps {
    className?: string
    parts?: LensMarkParts
    /** Extra shapes drawn on top, in the mark's own coordinates (the star sits at 612, 230). */
    children?: ReactNode
}

/** The mark's own coordinate space, cropped tight around the planet, its orbit and the star. */
const VIEW_BOX = '225 172 475 353'

/** The planet's outline, open at the top right where the star's trail crosses it. */
const RING = 'M584.4 297.6 A150 150 0 1 1 544.3 249.8'
/** The lit crescent inside the planet, thickest on the left. */
const CRESCENT = 'M429 238 A132 132 0 1 0 486 495.5 A138 138 0 0 1 429 238 Z'
/** The near half of the orbit, a swoosh that tapers to a point at each end. */
const ORBIT_FRONT = 'M-232 0 A232 48 0 0 0 232 0 A232 35 0 0 1 -232 0 Z'
/** The far half of the orbit, showing only where it clears the planet. */
const ORBIT_BACK = ['M-232 0 A232 42 0 0 1 -218 -14.4', 'M210 -17.7 A232 42 0 0 1 232 0']
const ORBIT_TRANSFORM = 'translate(462 352) rotate(-14)'

/** Three tapering streaks running from the star back towards the planet. */
const TRAILS = (() => {
    const direction = [-0.785, 0.619]
    const normal = [0.619, 0.785]
    const origin = [586, 263]
    const streaks: [number, number, number][] = [
        [0, 175, 7],
        [14, 120, 5],
        [-13, 95, 4]
    ]

    return streaks.map(([offset, length, width]) => {
        const sx = origin[0] + normal[0] * offset
        const sy = origin[1] + normal[1] * offset
        const ex = sx + direction[0] * length
        const ey = sy + direction[1] * length
        const hx = (normal[0] * width) / 2
        const hy = (normal[1] * width) / 2

        return `M${(sx + hx).toFixed(1)} ${(sy + hy).toFixed(1)} L${(sx - hx).toFixed(1)} ${(sy - hy).toFixed(1)} L${ex.toFixed(1)} ${ey.toFixed(1)} Z`
    })
})()

/** A five-pointed star, tilted a little as if caught mid-flight. */
const STAR = (() => {
    const cx = 612
    const cy = 230
    const points: string[] = []

    for (let i = 0; i < 10; i++) {
        const radius = i % 2 === 0 ? 50 : 20
        const angle = ((-90 - 8 + i * 36) * Math.PI) / 180

        points.push(`${(cx + Math.cos(angle) * radius).toFixed(1)} ${(cy + Math.sin(angle) * radius).toFixed(1)}`)
    }

    return `M${points.join(' L')} Z`
})()

/**
 * The CosmicLens mark: a crescent planet inside its outline, a sweeping orbit passing in front of it,
 * and a star launching away from it on three trails. Drawn as code so it stays sharp at any size and
 * recolours with the theme (red-light mode included).
 */
export const CosmicLensGlyph = ({ className, parts, children }: GlyphProps): ReactNode => {
    const maskId = `lens-mask-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`

    return (
        <svg viewBox={VIEW_BOX} aria-hidden className={cn('shrink-0 overflow-visible', className)}>
            <defs>
                {/* Where the orbit and the trails cross the planet, the planet is cut back so they read as passing in front. */}
                <mask id={maskId} maskUnits='userSpaceOnUse' x='150' y='100' width='650' height='500'>
                    <rect x='150' y='100' width='650' height='500' fill='#fff' />
                    <path d={ORBIT_FRONT} transform={ORBIT_TRANSFORM} fill='#000' stroke='#000' strokeWidth='14' strokeLinejoin='round' />
                    {TRAILS.map(trail => (
                        <path key={trail} d={trail} fill='#000' stroke='#000' strokeWidth='9' strokeLinejoin='round' />
                    ))}
                </mask>
            </defs>

            <g mask={`url(#${maskId})`}>
                <path d={RING} fill='none' stroke='var(--c-star)' strokeWidth='10' strokeLinecap='round' pathLength={1} className={parts?.ring} />
                <path d={CRESCENT} fill='var(--c-star)' className={parts?.crescent} />
            </g>

            {/* The class sits on an outer group so an animated CSS transform never replaces the orbit's own tilt. */}
            <g className={parts?.orbit}>
                <g transform={ORBIT_TRANSFORM}>
                    {ORBIT_BACK.map(arc => (
                        <path key={arc} d={arc} fill='none' stroke='var(--c-brass)' strokeWidth='4' strokeLinecap='round' />
                    ))}
                    <path d={ORBIT_FRONT} fill='var(--c-brass)' />
                </g>
            </g>

            <g fill='var(--c-brass)' className={parts?.trails}>
                {TRAILS.map(trail => (
                    <path key={trail} d={trail} />
                ))}
            </g>

            <path d={STAR} fill='none' stroke='var(--c-brass)' strokeWidth='7' strokeLinejoin='round' pathLength={1} className={parts?.star} />

            {children}
        </svg>
    )
}

interface WordmarkProps {
    className?: string
    glyphClassName?: string
}

export const Wordmark = ({ className, glyphClassName }: WordmarkProps): ReactNode => {
    return (
        <span className={cn('inline-flex items-center gap-2', className)}>
            <CosmicLensGlyph className={cn('h-8 w-auto', glyphClassName)} />
            <span className='font-brand text-[1.3rem] leading-none tracking-[0.01em] text-star'>
                CosmicLens
            </span>
        </span>
    )
}
